import React from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { Avatar, AvatarSize, Tag, Text } from 'components';
import { theme, toRem } from 'design';
import { DisbursementMethod } from 'types';

export interface IUserInfoProps {
  className?: string;
  dataCy?: string;
  disbursementMethod?: DisbursementMethod;
  hideAvatar?: boolean;
  image?: string;
  imageSize?: AvatarSize;
  info?: string | React.ReactNode;
  isLoading?: boolean;
  renderInfo?: () => React.ReactNode;
  username: string;
}

const Name = styled(Text).attrs({ size: 'small' })`
  font-weight: ${theme.font.weight.medium};
  font-size: ${toRem(14)};
  line-height: 100%;
  color: ${theme.color.neutral[900]};
  text-align: left;
  word-break: break-word;
`;

const UserAdditionalInfo = styled(Text)`
  font-size: ${toRem(12)};
  color: ${theme.color.neutral[500]};
  text-transform: uppercase;
  word-wrap: break-word;
  max-width: ${toRem(240)};
`;

const UserAvatar = styled(Avatar)`
  margin: 0 ${toRem(11)} 0 0;
`;

const UserInfoWrapper = styled.section`
  flex-flow: column nowrap;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  max-width: 100%;

  > *:not(:last-child) {
    margin-bottom: ${toRem(3)};

    @media (min-width: ${theme.breakpoint.small}px) {
      margin-bottom: ${toRem(6)};
    }
  }
`;

const UserInfoContainer = styled.div`
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: auto;
  height: auto;
`;

const UserInfoLayout = styled.div`
  width: 100%;
  max-width: 100%;
`;

const UserInfo: React.FC<IUserInfoProps> = ({ className = '', dataCy = 'user-info', disbursementMethod, hideAvatar, image, imageSize = 'medlarge', info, isLoading, renderInfo, username }) => {
  const renderDisbursementMethod = () => {
    if (!disbursementMethod) return null;
    if (isLoading) return <Skeleton width={50} />;
    return <Tag label={disbursementMethod} appearance={disbursementMethod} dataCy="user-info-disbursement-method" />;
  };

  const renderAdditionalInfo = () => {
    if (renderInfo) return renderInfo();

    if (!info) return null;
    if (isLoading) return <Skeleton width={50} />;
    return (
      <UserAdditionalInfo size="small" data-cy="user-info-additional-info">
        {info}
      </UserAdditionalInfo>
    );
  };

  return (
    <UserInfoContainer className={className} data-cy={dataCy}>
      <UserInfoLayout className="user-info-layout">
        {!hideAvatar && <UserAvatar data-cy="payee-avatar" size={imageSize} username={username} image={image} isLoading={isLoading} />}
        <UserInfoWrapper>
          <Name data-cy="payee-name">{isLoading ? <Skeleton width={77} /> : username}</Name>
          {renderAdditionalInfo()}
          {renderDisbursementMethod()}
        </UserInfoWrapper>
      </UserInfoLayout>
    </UserInfoContainer>
  );
};

export default UserInfo;
